<template>
  <div>
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>编辑账户信息</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field
              v-model="account.username"
              :rules="nameRules"
              label="账号"
              required
            ></v-text-field>

            <div class="d-flex align-center">
              <span class="grey--text text--darken-1">密码：</span>
              <v-btn
                color="primary"
                class="ml-2"
                @click="showChangePasswordDialog"
              >
                更改密码
              </v-btn>
            </div>

            <v-text-field
              v-model="account.email"
              :rules="emailRules"
              label="电子邮箱"
              required
            ></v-text-field>

            <v-autocomplete
              v-model="account.sourceChannelId"
              :rules="channelRules"
              :items="channels"
              item-text="name"
              item-value="id"
              label="来源渠道"
              required
            ></v-autocomplete>

            <v-menu
              ref="startMenu"
              v-model="startMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedStartTime"
                  label="启用时间"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                scrollable
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="startMenu = false">取消</v-btn>
                <v-btn text color="primary" @click="showStartTimePicker">
                  选择时间
                </v-btn>
              </v-date-picker>
            </v-menu>

            <v-menu
              ref="startTimeMenu"
              v-model="startTimeMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
            >
              <v-time-picker
                v-if="startTimeMenu"
                v-model="startTime"
                full-width
                format="24hr"
                use-seconds
                scrollable
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="startTimeMenu = false">取消</v-btn>
                <v-btn text color="primary" @click="saveStartTime">
                  确定
                </v-btn>
              </v-time-picker>
            </v-menu>

            <v-menu
              ref="endMenu"
              v-model="endMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedExpiredTime"
                  label="有效期限"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="expiredDate"
                scrollable
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="endMenu = false">取消</v-btn>
                <v-btn text color="primary" @click="showEndTimePicker">
                  选择时间
                </v-btn>
              </v-date-picker>
            </v-menu>

            <v-menu
              ref="endTimeMenu"
              v-model="endTimeMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
            >
              <v-time-picker
                v-if="endTimeMenu"
                v-model="expiredTime"
                full-width
                format="24hr"
                use-seconds
                scrollable
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="endTimeMenu = false">取消</v-btn>
                <v-btn text color="primary" @click="saveEndTime">
                  确定
                </v-btn>
              </v-time-picker>
            </v-menu>

            <v-select
              v-model="account.accountStatus.id"
              :items="statusList"
              item-text="name"
              item-value="id"
              label="账号状态"
            ></v-select>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDialog">取消</v-btn>
          <v-btn color="blue darken-1" text @click="saveChanges">储存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <change-password-dialog ref="changePasswordDialog" />
  </div>
</template>

<script>
import { userApi } from '@/api/user'
import ChangePasswordDialog from './ChangePasswordDialog.vue'
import dayjs from 'dayjs'

export default {
  name: "EditAccountPopup",
  components: {
    ChangePasswordDialog
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      valid: true,
      account: {
        accountStatus: {}
      },
      startDate: null,
      startTime: null,
      expiredDate: null,
      expiredTime: null,
      startMenu: false,
      startTimeMenu: false,
      endMenu: false,
      endTimeMenu: false,
      channels: [],
      statusList: [],
      passwordRules: [
        (v) => !!v || "密码是必填项",
        (v) => (v && v.length >= 8) || "密码至少需要8个字符",
      ],
      nameRules: [(v) => !!v || "姓名是必填项"],
      emailRules: [
        (v) => !!v || "电子邮箱是必填项",
        (v) => /.+@.+\..+/.test(v) || "请输入有效的电子邮件地址",
      ],
      channelRules: [(v) => v !== null && v !== undefined || "来源渠道是必填项"],
    };
  },
  computed: {
    formattedStartTime() {
      if (!this.startDate || !this.startTime) return ''
      return this.formatDateTime(this.startDate, this.startTime)
    },
    formattedExpiredTime() {
      if (!this.expiredDate || !this.expiredTime) return ''
      return this.formatDateTime(this.expiredDate, this.expiredTime)
    }
  },
  watch: {
    item: {
      handler(newVal) {
        this.account = { 
          ...newVal,
          accountStatus: newVal.accountStatus || {}
         };
        console.log('newVal = ', newVal);
        // 解析時間
        if (this.account.startTime) {
          const startDateTime = dayjs(this.account.startTime);
          this.startDate = startDateTime.format('YYYY-MM-DD');
          this.startTime = startDateTime.format('HH:mm:ss');
        }
        if (this.account.expiredTime) {
          const expiredDateTime = dayjs(this.account.expiredTime);
          this.expiredDate = expiredDateTime.format('YYYY-MM-DD');
          this.expiredTime = expiredDateTime.format('HH:mm:ss');
        }
      },
      immediate: true,
    },
    // 監聽格式化後的時間並更新 account
    formattedStartTime(val) {
      if (val) {
        this.account.startTime = val;
      }
    },
    formattedExpiredTime(val) {
      if (val) {
        this.account.expiredTime = val;
      }
    }
  },
  async created() {
    await this.fetchChannels();
    await this.fetchStatusList();
  },
  methods: {
    async fetchChannels() {
      try {
        const response = await userApi.getUserSourceChannels()
        this.channels = response.data.userSourceChannels
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || '未知错误';
        this.$toast.error('获取渠道列表失败：' + errorMessage);
      }
    },

    async fetchStatusList() {
      try {
        const response = await userApi.getAccountStatusList()
        this.statusList = response.data.accountStatusList
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || '未知错误';
        this.$toast.error('获取账号状态列表失败：' + errorMessage);
      }
    },

    async saveChanges() {
      if (!this.$refs.form.validate()) return;

      try {
        // 调用后端 API 更新账号
        await userApi.updateJobSeekerAccount(this.account);
        
        this.dialog = false;
        this.$emit("refresh-list");
        this.$toast.success('账号更新成功');
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || '未知错误';
        this.$toast.error('更新失败：' + errorMessage);
      }
    },

    closeDialog() {
    this.dialog = false;
    this.$nextTick(() => {
      // 重置所有表单数据
      this.startDate = null;
      this.startTime = null;
      this.expiredDate = null;
      this.expiredTime = null;
      this.startMenu = false;
      this.startTimeMenu = false;
      this.endMenu = false;
      this.endTimeMenu = false;
      this.account = {
        accountStatus: {}
      };
      // 重置表单验证
      this.$refs.form.resetValidation();
    });
  },

    showChangePasswordDialog() {
      this.$refs.changePasswordDialog.open(this.account.id);
    },

    formatDateTime(date, time) {
      if (!date || !time) return '';
      return dayjs(`${date} ${time}`).format('YYYY-MM-DD HH:mm:ss');
    },

    showStartTimePicker() {
      this.startMenu = false;
      this.$nextTick(() => {
        this.startTimeMenu = true;
      });
    },

    showEndTimePicker() {
      this.endMenu = false;
      this.$nextTick(() => {
        this.endTimeMenu = true;
      });
    },

    saveStartTime() {
      this.startTimeMenu = false;
    },

    saveEndTime() {
      this.endTimeMenu = false;
    }
  },
};
</script>   